/**
 * Bootstraps the short-lived, HttpOnly session used by the production edge
 * signaler.  Local development deliberately skips this step: the local
 * Socket.IO service does not have a Cloudflare challenge endpoint.
 */

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_ACTION = "turnstile-spin-v1";
const SCRIPT_TIMEOUT_MS = 20_000;
const CHALLENGE_TIMEOUT_MS = 90_000;
const TURNSTILE_LOAD_ERROR = "Cloudflare verification could not load. Allow challenges.cloudflare.com in your browser or disable the blocking extension, then retry.";
const TURNSTILE_START_ERROR = "Cloudflare verification could not start. Allow challenges.cloudflare.com in your browser or disable the blocking extension, then retry.";

interface TurnstileRenderOptions {
  sitekey: string;
  action: string;
  appearance: "interaction-only";
  execution: "execute";
  callback: (token: string) => void;
  retry: "auto";
  "retry-interval": number;
  "refresh-expired": "auto";
  "refresh-timeout": "auto";
  "before-interactive-callback": () => void;
  "after-interactive-callback": () => void;
  "error-callback": (code?: string | number) => boolean;
  "timeout-callback": () => void;
  "unsupported-callback": () => void;
}

interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string | number;
  execute(container: HTMLElement | string): void;
  remove?(widgetId: string | number): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptRequest: Promise<TurnstileApi> | null = null;
let sessionRequest: Promise<void> | null = null;
let sessionExpiresAt = 0;
const SESSION_REFRESH_WINDOW_MS = 60_000;

/** Ensure this browser has a verified session before opening production signaling. */
export const ensureOnlineSession = async (): Promise<void> => {
  if (shouldSkipChallenge() || sessionExpiresAt - Date.now() > SESSION_REFRESH_WINDOW_MS) return;
  if (sessionRequest) {
    await sessionRequest;
    return;
  }
  sessionRequest = bootstrapSession()
    .then((expiresAt) => { sessionExpiresAt = expiresAt; })
    .finally(() => {
      // Later calls can renew an expired cookie or retry a failed challenge.
      sessionRequest = null;
    });
  await sessionRequest;
};

const bootstrapSession = async (): Promise<number> => {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim();
  if (!siteKey) {
    throw new Error("Online play verification is not configured for this site.");
  }
  const token = await requestTurnstileToken(siteKey);
  let response: Response;
  try {
    response = await fetch("/api/session", {
      body: JSON.stringify({ token }),
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      method: "POST",
    });
  } catch {
    throw new Error("The online verification service could not be reached.");
  }
  if (response.ok) {
    const payload = await response.json() as unknown;
    if (isRecord(payload) && typeof payload.expiresAt === "number") return payload.expiresAt;
    throw new Error("Browser verification returned an invalid session.");
  }
  let message = "Browser verification failed. Please retry.";
  try {
    const payload = await response.json() as unknown;
    if (isRecord(payload) && typeof payload.error === "string" && payload.error.trim()) message = payload.error;
  } catch {
    // Keep the safe, generic message when the response is not JSON.
  }
  if (response.status === 429) message = "Too many verification attempts. Please wait a minute and retry.";
  throw new Error(message);
};

const requestTurnstileToken = async (siteKey: string): Promise<string> => {
  const turnstile = await loadTurnstile();
  const container = document.createElement("div");
  container.setAttribute("aria-label", "Browser verification");
  container.style.cssText = [
    "position:fixed",
    "left:50%",
    "bottom:24px",
    "z-index:2000",
    "width:min(300px,calc(100vw - 32px))",
    "min-height:65px",
    "opacity:0",
    "pointer-events:none",
    "transform:translateX(-50%)",
    "transition:opacity 150ms ease",
  ].join(";");
  document.body.append(container);

  return new Promise<string>((resolve, reject) => {
    let widgetId: string | number | null = null;
    let settled = false;
    const timer = window.setTimeout(() => finish(new Error("Browser verification timed out. Please retry.")), CHALLENGE_TIMEOUT_MS);
    const finish = (error?: Error, token?: string): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (widgetId !== null) turnstile.remove?.(widgetId);
      container.remove();
      if (error) reject(error);
      else if (token) resolve(token);
      else reject(new Error("Browser verification returned no token."));
    };
    const renderAndExecute = (): void => {
      if (settled) return;
      try {
        widgetId = turnstile.render(container, {
          action: TURNSTILE_ACTION,
          appearance: "interaction-only",
          "after-interactive-callback": () => setChallengeVisible(container, false),
          "before-interactive-callback": () => setChallengeVisible(container, true),
          callback: (token) => finish(undefined, token),
          execution: "execute",
          "error-callback": (code) => {
            if (String(code ?? "").startsWith("110")) {
              finish(new Error("Online verification is misconfigured for this site."));
            }
            return true;
          },
          "refresh-expired": "auto",
          "refresh-timeout": "auto",
          retry: "auto",
          "retry-interval": 5_000,
          sitekey: siteKey,
          "timeout-callback": () => setChallengeVisible(container, true),
          "unsupported-callback": () => finish(new Error("This browser cannot complete online verification. Update the browser or use LAN mode.")),
        });
        // Execute against the rendered container. Turnstile reserves widget IDs
        // for lifecycle calls such as reset, getResponse, and remove.
        turnstile.execute(container);
      } catch {
        finish(new Error(TURNSTILE_START_ERROR));
      }
    };
    try {
      // The script load event guarantees the API is initialized. Calling
      // turnstile.ready here can hit Cloudflare's pre-load hook and throw.
      renderAndExecute();
    } catch {
      finish(new Error(TURNSTILE_START_ERROR));
    }
  });
};

const loadTurnstile = (): Promise<TurnstileApi> => {
  // Cloudflare may install a pre-load hook at window.turnstile before api.js
  // has finished loading. Waiting for the script event avoids calling ready()
  // on that hook, which fails with "could not start" in some browsers.
  scriptRequest ??= new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src^="${TURNSTILE_SCRIPT}"]`);
    const script = existing ?? document.createElement("script");
    let settled = false;
    const timer = window.setTimeout(() => finish(new Error(TURNSTILE_LOAD_ERROR)), SCRIPT_TIMEOUT_MS);
    const finish = (error?: Error): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (error) reject(error);
      else if (isTurnstileApi(window.turnstile)) resolve(window.turnstile);
      else reject(new Error(TURNSTILE_LOAD_ERROR));
    };
    script.addEventListener("load", () => finish());
    script.addEventListener("error", () => finish(new Error(TURNSTILE_LOAD_ERROR)));
    if (!existing) {
      script.async = true;
      script.defer = true;
      script.src = TURNSTILE_SCRIPT;
      document.head.append(script);
    } else if (isLoadedScript(script) && isTurnstileApi(window.turnstile)) {
      finish();
    }
  }).catch((error: unknown) => {
    scriptRequest = null;
    throw error;
  });
  return scriptRequest;
};

const shouldSkipChallenge = (): boolean => {
  if (import.meta.env.DEV) return true;
  if (typeof window === "undefined") return false;
  return isPrivateHost(window.location.hostname);
};

const isPrivateHost = (hostname: string): boolean => {
  const host = hostname.replace(/^\[|\]$/g, "").toLowerCase();
  if (host === "localhost" || host === "::1" || host.endsWith(".local")) return true;
  if (host.includes(":")) return /^(?:f[cd]|fe[89ab])/.test(host);
  const octets = host.split(".").map(Number);
  if (octets.length !== 4 || octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)) return false;
  return octets[0] === 10
    || octets[0] === 127
    || (octets[0] === 169 && octets[1] === 254)
    || (octets[0] === 172 && octets[1]! >= 16 && octets[1]! <= 31)
    || (octets[0] === 192 && octets[1] === 168);
};

const isLoadedScript = (script: HTMLScriptElement): boolean => {
  const state = (script as HTMLScriptElement & { readyState?: string }).readyState;
  return state === "loaded" || state === "complete";
};

const setChallengeVisible = (container: HTMLElement, visible: boolean): void => {
  container.style.opacity = visible ? "1" : "0";
  container.style.pointerEvents = visible ? "auto" : "none";
};

const isTurnstileApi = (value: TurnstileApi | undefined): value is TurnstileApi => value !== undefined
  && typeof value.render === "function"
  && typeof value.execute === "function";

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;
